/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

// Enhanced Dashboard.jsx - Comprehensive Admin Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  Cell, ReferenceLine
} from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Skeleton } from "../ui/skeleton";
import { Alert, AlertDescription } from "../ui/alert";
import { format } from 'date-fns';
import { useToast } from "../ui/use-toast";

// Color constants for consistent chart styling
const COLORS = {
  primary: '#2563eb',    // blue-600
  secondary: '#f97316',  // orange-500
  success: '#22c55e',    // green-500
  danger: '#ef4444',     // red-500
  warning: '#f59e0b',    // amber-500
  info: '#0ea5e9',       // sky-500
  accent: '#8b5cf6',     // violet-500
  chartBars: ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'],
  pieColors: ['#2563eb', '#f97316', '#22c55e', '#ef4444', '#f59e0b', '#0ea5e9']
};

const Dashboard = () => {
  // State management
  const [stats, setStats] = useState({
    userStats: { total: 0, students: 0, teachers: 0, admins: 0 },
    testStats: { total: 0, active: 0, inactive: 0 },
    questionStats: { total: 0, active: 0, inactive: 0 },
    cohortStats: { total: 0 },
    performanceStats: { averageScore: 0, passRate: 0 }
  });
  const [difficultQuestions, setDifficultQuestions] = useState([]);
  const [testPerformance, setTestPerformance] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [scoreDistribution, setScoreDistribution] = useState([]);
  const [recentTestResults, setRecentTestResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { toast } = useToast();

  // Fetch all data needed for the dashboard
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);

      // 1. Fetch basic stats from existing endpoint
      const basicStatsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/dashboard`);
      if (!basicStatsResponse.ok) throw new Error('Failed to fetch basic stats');
      const basicStatsData = await basicStatsResponse.json();

      // 2. Fetch user statistics
      const usersResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users`);
      if (!usersResponse.ok) throw new Error('Failed to fetch users');
      const usersData = await usersResponse.json();

      // 3. Fetch test data
      const testsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests?includeInactive=true`);
      if (!testsResponse.ok) throw new Error('Failed to fetch tests');
      const testsData = await testsResponse.json();

      // 4. Fetch student progress data
      const progressResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/students-progress`);
      if (!progressResponse.ok) throw new Error('Failed to fetch student progress');
      const progressData = await progressResponse.json();

      // 5. Fetch real user activity data
      const activityResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/user-activity?days=30`);
      if (!activityResponse.ok) throw new Error('Failed to fetch user activity');
      const activityData = await activityResponse.json();

      // Process and transform data for dashboard components
      processData(basicStatsData, usersData, testsData, progressData, activityData);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
      toast({
        title: 'Error',
        description: 'Failed to load dashboard statistics',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Process and transform the fetched data for visualization
  const processData = (basicStats, usersData, testsData, progressData, activityData) => {
    // 1. Aggregate user statistics
    const userStats = {
      total: usersData.length,
      students: usersData.filter(user => user.role === 'student').length,
      teachers: usersData.filter(user => user.role === 'teacher').length,
      admins: usersData.filter(user => user.role === 'admin' || user.is_admin).length
    };

    // 2. Aggregate test statistics
    const testStats = {
      total: testsData.length,
      active: testsData.filter(test => test.is_active).length,
      inactive: testsData.filter(test => !test.is_active).length
    };

    // 3. Calculate question statistics from basic stats
    const questionStats = {
      total: basicStats.totalQuestions,
      active: basicStats.totalQuestions * 0.9, // Approximation if not available
      inactive: basicStats.totalQuestions * 0.1 // Approximation if not available
    };

    // 4. Process test performance data
    const testPerformanceData = [];
    const testMap = new Map();
    
    // Group test results by test
    progressData.forEach(student => {
      student.test_results.forEach(result => {
        if (!testMap.has(result.test_id)) {
          testMap.set(result.test_id, {
            id: result.test_id,
            name: result.test_name,
            scores: [],
            results: [], // Store full result objects for passed status
            attempts: 0,
            timeTaken: []
          });
        }
        
        const testData = testMap.get(result.test_id);
        testData.scores.push(parseFloat(result.raw_percentage));
        testData.results.push(result); // Store full result for passed status
        testData.attempts += 1;
        if (result.time_spent) {
          testData.timeTaken.push(parseInt(result.time_spent));
        }
      });
    });
    
    // Calculate statistics for each test
    testMap.forEach(test => {
      const totalScores = test.scores.reduce((sum, score) => sum + score, 0);
      const avgScore = test.scores.length > 0 ? totalScores / test.scores.length : 0;
      // Use database passed status for pass rate calculation
      const passRate = test.results ? (test.results.filter(result => result.passed === true).length / test.results.length * 100) : 0;
      const avgTime = test.timeTaken.length > 0 ? 
        test.timeTaken.reduce((sum, time) => sum + time, 0) / test.timeTaken.length : 0;
      
      testPerformanceData.push({
        id: test.id,
        name: test.name,
        averageScore: avgScore.toFixed(1),
        passRate: passRate.toFixed(1),
        attempts: test.attempts,
        avgCompletionTime: (avgTime / 60).toFixed(1) // Convert to minutes
      });
    });
    
    // 5. Identify difficult questions
    const questionDifficulty = [];
    progressData.forEach(student => {
      student.test_results.forEach(result => {
        if (result.failed_questions) {
          result.failed_questions.forEach(fq => {
            const existingQuestion = questionDifficulty.find(q => q.id === fq.question_id);
            if (existingQuestion) {
              existingQuestion.failCount += 1;
            } else {
              questionDifficulty.push({
                id: fq.question_id,
                text: fq.question_text?.substring(0, 50) + '...' || `Question ${fq.question_id}`,
                failCount: 1
              });
            }
          });
        }
      });
    });
    
    // Sort by fail count and take top 10
    const difficultQs = questionDifficulty
      .sort((a, b) => b.failCount - a.failCount)
      .slice(0, 10)
      .map(q => ({
        id: q.id,
        text: q.text,
        failureRate: q.failCount
      }));

    // 6. Create score distribution data
    const allScores = [];
    progressData.forEach(student => {
      student.test_results.forEach(result => {
        if (result.raw_percentage) {
          allScores.push(parseFloat(result.raw_percentage));
        }
      });
    });
    
    const scoreDistributionData = [
      { range: '0-9', count: 0 },
      { range: '10-19', count: 0 },
      { range: '20-29', count: 0 },
      { range: '30-39', count: 0 },
      { range: '40-49', count: 0 },
      { range: '50-59', count: 0 },
      { range: '60-69', count: 0 },
      { range: '70-79', count: 0 },
      { range: '80-89', count: 0 },
      { range: '90-100', count: 0 }
    ];
    
    allScores.forEach(score => {
      if (score < 10) scoreDistributionData[0].count++;
      else if (score < 20) scoreDistributionData[1].count++;
      else if (score < 30) scoreDistributionData[2].count++;
      else if (score < 40) scoreDistributionData[3].count++;
      else if (score < 50) scoreDistributionData[4].count++;
      else if (score < 60) scoreDistributionData[5].count++;
      else if (score < 70) scoreDistributionData[6].count++;
      else if (score < 80) scoreDistributionData[7].count++;
      else if (score < 90) scoreDistributionData[8].count++;
      else scoreDistributionData[9].count++;
    });
    
    // 7. Process real user activity data (last 30 days)
    const processedActivityData = activityData.map(day => ({
      date: format(new Date(day.date), 'MMM d'),
      logins: day.logins,
      testsTaken: day.testsTaken
    }));
    
    // 8. Calculate overall performance statistics
    const allTestScores = allScores.length > 0 ? 
      allScores.reduce((sum, score) => sum + score, 0) / allScores.length : 0;
    // Calculate overall pass rate using database passed status
    const allResults = [];
    progressData.forEach(student => {
      student.test_results.forEach(result => allResults.push(result));
    });
    const overallPassRate = allResults.length > 0 ?
      (allResults.filter(result => result.passed === true).length / allResults.length * 100) : 0;
    
    // 9. Get recent test results (use existing data from basic stats)
    
    // Set all the processed data to state
    setStats({
      userStats,
      testStats,
      questionStats,
      cohortStats: { total: basicStats.totalTests || 0 }, // Placeholder if not available
      performanceStats: { 
        averageScore: allTestScores, 
        passRate: overallPassRate 
      }
    });
    setDifficultQuestions(difficultQs);
    setTestPerformance(testPerformanceData);
    setUserActivity(processedActivityData);
    setScoreDistribution(scoreDistributionData);
    setRecentTestResults(basicStats.recentTestResults || []);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDashboardData();
    // Set up refresh interval (every 5 minutes)
    const intervalId = setInterval(fetchDashboardData, 300000);
    
    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [fetchDashboardData]);

  // Rendering loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-5 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-10 w-1/3" />
                <Skeleton className="h-4 w-3/4 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/4" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[300px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Main dashboard render
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Summary Cards */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.userStats.total}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats.userStats.students} Students • {stats.userStats.teachers} Teachers • {stats.userStats.admins} Admins
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Tests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.testStats.total}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats.testStats.active} Active • {stats.testStats.inactive} Inactive
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.questionStats.total}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {Math.round(stats.questionStats.active)} Active • {Math.round(stats.questionStats.inactive)} Inactive
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overall Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.performanceStats.averageScore.toFixed(1)}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats.performanceStats.passRate.toFixed(1)}% Pass Rate
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analysis Tabs */}
      <Tabs defaultValue="performance" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="performance">Performance Analysis</TabsTrigger>
          <TabsTrigger value="users">User Activity</TabsTrigger>
          <TabsTrigger value="details">Test Insights</TabsTrigger>
          <TabsTrigger value="difficulties">Problem Areas</TabsTrigger>
        </TabsList>
        
        <TabsContent value="performance">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Score Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Score Distribution</CardTitle>
                <CardDescription>Distribution of test scores across all students</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={scoreDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <ReferenceLine x="90-100" stroke={COLORS.success} label="Pass Threshold" />
                      <Bar dataKey="count" name="Students">
                        {scoreDistribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.range === '90-100' ? COLORS.success : COLORS.primary} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Test Performance Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Test Performance</CardTitle>
                <CardDescription>Average scores and pass rates by test</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={testPerformance.slice(0, 5)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis type="category" dataKey="name" width={120} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="averageScore" name="Avg. Score %" fill={COLORS.primary} />
                      <Bar dataKey="passRate" name="Pass Rate %" fill={COLORS.success} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="users">
          <div className="grid md:grid-cols-2 gap-6">
            {/* User Activity Chart */}
            <Card>
              <CardHeader>
                <CardTitle>User Activity (30-Day Trend)</CardTitle>
                <CardDescription>Daily active users and tests taken</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={userActivity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="logins" stroke={COLORS.primary} name="Active Users" />
                      <Line type="monotone" dataKey="testsTaken" stroke={COLORS.secondary} name="Tests Taken" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* User Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>User Role Distribution</CardTitle>
                <CardDescription>Breakdown of user accounts by role</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Students', value: stats.userStats.students },
                          { name: 'Teachers', value: stats.userStats.teachers },
                          { name: 'Admins', value: stats.userStats.admins }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {[
                          { name: 'Students', value: stats.userStats.students },
                          { name: 'Teachers', value: stats.userStats.teachers },
                          { name: 'Admins', value: stats.userStats.admins }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.pieColors[index % COLORS.pieColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="details">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Most Attempted Tests */}
            <Card>
              <CardHeader>
                <CardTitle>Most Attempted Tests</CardTitle>
                <CardDescription>Tests with the highest number of attempts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={testPerformance.sort((a, b) => b.attempts - a.attempts).slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="attempts" name="Attempts" fill={COLORS.info}>
                        {testPerformance.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.chartBars[index % COLORS.chartBars.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Test Completion Times */}
            <Card>
              <CardHeader>
                <CardTitle>Average Completion Times</CardTitle>
                <CardDescription>Average time to complete each test (minutes)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={testPerformance.sort((a, b) => b.avgCompletionTime - a.avgCompletionTime).slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="avgCompletionTime" name="Avg. Time (min)" fill={COLORS.accent}>
                        {testPerformance.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.chartBars[index % COLORS.chartBars.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="difficulties">
          {/* Most Difficult Questions */}
          <Card>
            <CardHeader>
              <CardTitle>Most Challenging Questions</CardTitle>
              <CardDescription>Questions with the highest failure rates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={difficultQuestions} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="text" width={300} />
                    <Tooltip />
                    <Bar dataKey="failureRate" name="Failure Count" fill={COLORS.danger} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Recent Activities */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Test Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {recentTestResults.map((result, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b">
                <div className="font-medium">{result.user_name}</div>
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground mr-2">{result.test_name}:</span>
                  <span className={`font-medium ${result.passed === true ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(result.score).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
            {recentTestResults.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">No recent test results</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;